<template>
	<div class="tab" @click="handleStageClick">
		<div class="tab__bg" aria-hidden="true">
			<canvas ref="particles" class="tab__particles"></canvas>
			<span class="tab__blob tab__blob--1"></span>
			<span class="tab__blob tab__blob--2"></span>
			<span class="tab__blob tab__blob--3"></span>
		</div>

		<span
			v-for="word in floatingWords"
			:key="word.id"
			class="tab__word"
			:style="word.style"
			aria-hidden="true"
		>{{ word.text }}</span>

		<div class="tab__toolbar">
			<button type="button" class="tab__tool" @click="goHome">博客首页</button>
			<button
				v-if="isAdmin"
				type="button"
				class="tab__tool"
				:class="{'tab__tool--active': editMode}"
				@click="toggleEdit"
			>{{ editMode ? '完成编辑' : '编辑书签' }}</button>
			<button v-if="isAdmin && editMode" type="button" class="tab__tool" @click="exportData">导出 JSON</button>
		</div>

		<main class="tab__main">
			<section class="tab__clock">
				<div class="tab__time">{{ clockHours }}<span class="tab__colon" aria-hidden="true">:</span>{{ clockMinutes }}<span class="tab__seconds">{{ clockSeconds }}</span></div>
				<div class="tab__date">
					<span>{{ dateText }}</span>
					<span class="tab__greeting">{{ greeting }}</span>
				</div>
			</section>

			<section class="tab__search">
				<svg class="tab__search-icon" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
					<circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" stroke-width="1.8"/>
					<line x1="16.5" y1="16.5" x2="21" y2="21" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
				</svg>
				<input
					v-model="keyword"
					class="tab__search-input"
					type="text"
					placeholder="搜索书签，或按回车使用搜索引擎"
					@keyup.enter="submitSearch"
				>
				<select v-model="searchEngine" class="tab__engine" @change="changeEngine">
					<option v-for="engine in searchEngines" :key="engine.key" :value="engine.key">{{ engine.name }}</option>
				</select>
			</section>

			<section v-if="frequentBookmarks.length" class="tab__frequent" aria-label="常用书签">
				<span class="tab__frequent-label">⚡ 常用</span>
				<div class="tab__frequent-list">
					<button
						v-for="item in frequentBookmarks"
						:key="item.id"
						type="button"
						class="tab__frequent-item"
						:title="`${item.title} · ${item.clicks || 0} 次访问`"
						@click="openBookmark(item)"
					>
						<span class="tab__frequent-icon">
							<img v-if="item.iconType === 'img'" :src="item.iconValue" alt="" @error="handleFrequentIconError(item)">
							<span v-else-if="item.iconType === 'emoji'" class="tab__frequent-emoji">{{ item.iconValue }}</span>
							<span v-else class="tab__frequent-letter" :style="{background: item.letterColor}">{{ item.letter }}</span>
						</span>
						<span class="tab__frequent-title">{{ item.title }}</span>
					</button>
				</div>
			</section>

			<section v-if="loading" class="tab__state">正在加载书签...</section>
			<section v-else-if="loadError" class="tab__state">
				<p>{{ loadError }}</p>
				<button type="button" class="tab__btn" @click="fetchData">重新加载</button>
			</section>
			<section v-else-if="!categories.length" class="tab__state">
				<p>🧭 这里还没有书签</p>
				<button v-if="editMode" type="button" class="tab__btn" @click="addCategory">创建第一个分组</button>
				<p v-else class="tab__state-hint">博主登录后可添加书签</p>
			</section>
			<section v-else ref="dash" class="tab__dash">
				<p v-if="keywordActive && !hasVisibleResults" class="tab__state tab__state--inline">没有匹配的书签</p>

				<button
					v-if="pages.length > 1"
					type="button"
					class="tab__arrow tab__arrow--prev"
					title="上一页"
					@click="turnPage(-1)"
					@dragenter.prevent="dragEnterPage(-1)"
					@dragleave="clearPageTimer"
				>‹</button>

				<transition name="tab-page" mode="out-in">
					<div :key="pageIndex" class="tab__page" :class="{'tab__page--narrow': gridDims.narrow}" :style="pageStyle">
						<CategoryWidget
							v-for="item in currentPageItems"
							:key="item.category.id"
							:category="item.category"
							:bookmarks="visibleBookmarks(item.category.id)"
							:total="countOf(item.category.id)"
							:edit-mode="editMode"
							:placement="item"
							:dragging="Boolean(draggingCategory && draggingCategory.id === item.category.id)"
							:drag-active="Boolean(draggingBookmark || draggingCategory)"
							:dragging-bookmark="draggingBookmark"
							@open="openBookmark"
							@edit-bookmark="editBookmark"
							@remove-bookmark="removeBookmark"
							@add-bookmark="addBookmark"
							@edit-category="editCategory"
							@remove-category="removeCategory"
							@resize="resizeCategory"
							@expand="openGroupDialog"
							@widget-drag-start="handleCategoryDragStart"
							@card-drag-start="handleBookmarkDragStart"
							@card-drop="handleCardDrop"
							@body-drop="handleBodyDrop"
							@drag-end="resetDrag"
						/>
					</div>
				</transition>

				<button
					v-if="pages.length > 1"
					type="button"
					class="tab__arrow tab__arrow--next"
					title="下一页"
					@click="turnPage(1)"
					@dragenter.prevent="dragEnterPage(1)"
					@dragleave="clearPageTimer"
				>›</button>

				<div v-if="pages.length > 1" class="tab__dots">
					<button
						v-for="(page, index) in pages"
						:key="index"
						type="button"
						class="tab__dot"
						:class="{'tab__dot--active': index === pageIndex}"
						:title="'第 ' + (index + 1) + ' 页'"
						@click="pageIndex = index"
					></button>
				</div>

				<button v-if="editMode" type="button" class="tab__add-category" @click="addCategory">＋ 新增分组</button>
			</section>
		</main>

		<EditDialog
			:visible="dialogVisible"
			:type="dialogType"
			:item="dialogItem"
			:categories="categories"
			:default-category-id="dialogCategoryId"
			:saving="saving"
			@close="closeDialog"
			@save="handleDialogSave"
		/>

		<GroupDialog
			:visible="groupDialogVisible"
			:category="groupDialogCategory"
			:bookmarks="groupDialogBookmarks"
			:edit-mode="editMode"
			@close="closeGroupDialog"
			@open="openBookmark"
			@edit-bookmark="editBookmark"
			@remove-bookmark="removeBookmark"
			@add-bookmark="addBookmark"
		/>
	</div>
</template>

<script>
	import CategoryWidget from './components/CategoryWidget'
	import EditDialog from './components/EditDialog'
	import GroupDialog from './components/GroupDialog'
	import {
		getBookmarks as getBookmarksApi,
		clickBookmark,
		saveBookmark as saveBookmarkApi,
		updateBookmark as updateBookmarkApi,
		deleteBookmark as deleteBookmarkApi,
		saveBookmarkCategory as saveCategoryApi,
		updateBookmarkCategory as updateCategoryApi,
		updateBookmarkCategorySize as updateCategorySizeApi,
		deleteBookmarkCategory as deleteCategoryApi,
		sortBookmarks as sortBookmarksApi,
		sortBookmarkCategories as sortCategoriesApi,
		exportBookmarks as exportBookmarksApi
	} from '@/api/bookmark'

	/**
	 * 搜索引擎列表
	 */
	const SEARCH_ENGINES = [
		{key: 'bing', name: 'Bing', url: 'https://www.bing.com/search?q='},
		{key: 'google', name: 'Google', url: 'https://www.google.com/search?q='},
		{key: 'baidu', name: '百度', url: 'https://www.baidu.com/s?wd='}
	]

	/**
	 * 星期名称
	 */
	const WEEK_NAMES = ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六']

	/**
	 * 分组尺寸与网格跨度的映射
	 */
	const CATEGORY_SIZES = {
		small: {w: 1, h: 1},
		wide: {w: 2, h: 1},
		tall: {w: 1, h: 2},
		large: {w: 2, h: 2}
	}

	/**
	 * 尺寸循环切换顺序
	 */
	const SIZE_ORDER = ['small', 'wide', 'tall', 'large']

	/**
	 * 分组卡片最小宽度（px），用于计算一页可容纳的列数
	 */
	const WIDGET_MIN_WIDTH = 330

	/**
	 * 分组卡片最小高度（px），用于计算一页可容纳的行数
	 */
	const WIDGET_MIN_HEIGHT = 240

	/**
	 * 仪表盘网格间距（px）
	 */
	const DASH_GAP = 20

	/**
	 * 仪表盘最大列数
	 */
	const MAX_GRID_COLS = 5

	/**
	 * 仪表盘最大行数（高屏下最多 3 行，保证分组不会向下溢出屏幕）
	 */
	const MAX_GRID_ROWS = 3

	/**
	 * 窄屏（手机）判定宽度（px），窄屏下分组纵向堆叠
	 */
	const NARROW_WIDTH = 640

	/**
	 * 窄屏模式下分组的固定行高（px）
	 */
	const NARROW_ROW_HEIGHT = 240

	/**
	 * 拖拽悬停翻页箭头时的切换延时（ms）
	 */
	const PAGE_SWITCH_DELAY = 500

	/**
	 * 判定为常用书签的最少点击次数
	 */
	const FREQUENT_MIN_CLICKS = 5

	/**
	 * 常用书签最多展示个数
	 */
	const FREQUENT_MAX_COUNT = 10

	/**
	 * 社会主义核心价值观词组，点击页面时依次浮现
	 */
	const CORE_VALUES = ['富强', '民主', '文明', '和谐', '自由', '平等', '公正', '法治', '爱国', '敬业', '诚信', '友善']

	/**
	 * 点击价值观文字的存活时长（ms）
	 */
	const WORD_LIFETIME = 1300

	/**
	 * 页面同时存在的价值观文字数量上限
	 */
	const MAX_FLOATING_WORDS = 20

	/**
	 * 粒子之间连线的最大距离（px）
	 */
	const PARTICLE_LINK_DISTANCE = 130

	/**
	 * 粒子与鼠标连线的最大距离（px）
	 */
	const PARTICLE_POINTER_DISTANCE = 170

	export default {
		name: 'BookmarkTab',
		components: {CategoryWidget, EditDialog, GroupDialog},
		data() {
			return {
				categories: [],
				bookmarks: [],
				keyword: '',
				loading: true,
				loadError: '',
				isAdmin: Boolean(window.localStorage.getItem('adminToken')),
				editMode: false,
				now: new Date(),
				clockTimer: null,
				searchEngines: SEARCH_ENGINES,
				searchEngine: window.localStorage.getItem('tab:searchEngine') || 'bing',
				dialogVisible: false,
				dialogType: 'bookmark',
				dialogItem: null,
				dialogCategoryId: null,
				saving: false,
				draggingBookmark: null,
				draggingCategory: null,
				groupDialogVisible: false,
				groupDialogCategory: null,
				gridDims: {cols: 3, rows: 2, narrow: false},
				pageIndex: 0,
				dashObserver: null,
				pageTimer: null,
				failedFrequentIcons: [],
				floatingWords: [],
				wordSequence: 0,
				coreValueIndex: 0
			}
		},
		computed: {
			/**
			 * 时钟小时数（两位数字）
			 */
			clockHours() {
				return String(this.now.getHours()).padStart(2, '0')
			},
			/**
			 * 时钟分钟数（两位数字）
			 */
			clockMinutes() {
				return String(this.now.getMinutes()).padStart(2, '0')
			},
			/**
			 * 时钟秒数（两位数字，小号展示）
			 */
			clockSeconds() {
				return String(this.now.getSeconds()).padStart(2, '0')
			},
			/**
			 * 日期文本
			 */
			dateText() {
				const year = this.now.getFullYear()
				const month = this.now.getMonth() + 1
				const day = this.now.getDate()
				return `${year}年${month}月${day}日 ${WEEK_NAMES[this.now.getDay()]}`
			},
			/**
			 * 问候语
			 */
			greeting() {
				const hour = this.now.getHours()
				if (hour < 6 || hour >= 23) {
					return '夜深了，注意休息'
				}
				if (hour < 9) {
					return '早上好'
				}
				if (hour < 12) {
					return '上午好'
				}
				if (hour < 14) {
					return '中午好'
				}
				if (hour < 18) {
					return '下午好'
				}
				return '晚上好'
			},
			/**
			 * 导出文件名中的日期
			 */
			dateStamp() {
				const year = this.now.getFullYear()
				const month = String(this.now.getMonth() + 1).padStart(2, '0')
				const day = String(this.now.getDate()).padStart(2, '0')
				return `${year}${month}${day}`
			},
			/**
			 * 是否处于关键词过滤状态
			 */
			keywordActive() {
				return Boolean(this.keyword.trim())
			},
			/**
			 * 参与仪表盘排布的分组：编辑模式或无关键词时为全部分组
			 */
			layoutCategories() {
				if (this.editMode || !this.keyword.trim()) {
					return this.categories
				}
				return this.categories.filter(category => this.visibleBookmarks(category.id).length > 0)
			},
			/**
			 * 搜索关键词下是否存在匹配的书签
			 */
			hasVisibleResults() {
				return this.categories.some(category => this.visibleBookmarks(category.id).length > 0)
			},
			/**
			 * 常用书签：点击次数达到阈值后按热度展示，控制显示个数
			 */
			frequentBookmarks() {
				if (this.keywordActive) {
					return []
				}
				return this.bookmarks
					.filter(bookmark => (bookmark.clicks || 0) >= FREQUENT_MIN_CLICKS)
					.sort((a, b) => (b.clicks || 0) - (a.clicks || 0))
					.slice(0, FREQUENT_MAX_COUNT)
					.map(bookmark => this.buildFrequentItem(bookmark))
			},
			/**
			 * 将分组按尺寸装箱到一页网格，放不下时拆分多页
			 */
			pages() {
				const list = this.layoutCategories
				const narrow = this.gridDims.narrow
				const cols = this.gridDims.cols
				const rows = narrow ? Math.max(1, list.length * 2) : this.gridDims.rows
				const pages = []
				let cells = this.makeCells(rows, cols)
				let currentPage = []
				list.forEach(category => {
					const size = CATEGORY_SIZES[category.size] || CATEGORY_SIZES.small
					const w = Math.min(size.w, cols)
					const h = Math.min(size.h, rows)
					let placed = this.findPlacement(cells, rows, cols, w, h)
					if (!placed) {
						pages.push(currentPage)
						cells = this.makeCells(rows, cols)
						currentPage = []
						placed = this.findPlacement(cells, rows, cols, w, h)
					}
					if (placed) {
						this.occupyCells(cells, placed.r, placed.c, w, h)
						currentPage.push({category, c: placed.c + 1, r: placed.r + 1, w, h})
					}
				})
				if (currentPage.length || !pages.length) {
					pages.push(currentPage)
				}
				return pages
			},
			/**
			 * 当前页的分组排布结果
			 */
			currentPageItems() {
				return this.pages[this.pageIndex] || []
			},
			/**
			 * 当前页网格样式
			 */
			pageStyle() {
				const dims = this.gridDims
				if (dims.narrow) {
					return {
						gridTemplateColumns: '1fr',
						gridAutoRows: `${NARROW_ROW_HEIGHT}px`
					}
				}
				return {
					gridTemplateColumns: `repeat(${dims.cols}, 1fr)`,
					gridTemplateRows: `repeat(${Math.max(1, dims.rows)}, 1fr)`
				}
			},
			/**
			 * 分组弹窗中展示的书签列表（跟随页面关键词过滤）
			 */
			groupDialogBookmarks() {
				if (!this.groupDialogCategory) {
					return []
				}
				return this.visibleBookmarks(this.groupDialogCategory.id)
			}
		},
		watch: {
			/**
			 * 页数变化时修正当前页码，避免越界
			 */
			pages(value) {
				if (this.pageIndex > value.length - 1) {
					this.pageIndex = Math.max(0, value.length - 1)
				}
			}
		},
		mounted() {
			this.fetchData()
			this.clockTimer = window.setInterval(() => {
				this.now = new Date()
			}, 1000)
			this.initParticles()
		},
		beforeDestroy() {
			if (this.clockTimer) {
				window.clearInterval(this.clockTimer)
				this.clockTimer = null
			}
			this.teardownParticles()
			this.floatingWords.forEach(word => window.clearTimeout(word.timer))
			this.floatingWords = []
			this.teardownDashObserver()
			this.clearPageTimer()
		},
		methods: {
			/**
			 * 加载分类与书签数据
			 */
			fetchData() {
				this.loading = true
				this.loadError = ''
				getBookmarksApi().then(res => {
					if (res.code !== 200) {
						this.loadError = res.msg || '加载失败'
						return
					}
					const data = res.data || {}
					this.categories = data.categories || []
					this.bookmarks = data.bookmarks || []
				}).catch(() => {
					this.loadError = '加载失败，请稍后重试'
				}).finally(() => {
					this.loading = false
					this.$nextTick(() => this.setupDashObserver())
				})
			},
			/**
			 * 返回博客首页
			 */
			goHome() {
				this.$router.push('/home')
			},
			/**
			 * 切换编辑模式
			 */
			toggleEdit() {
				if (!this.isAdmin) {
					return
				}
				this.editMode = !this.editMode
				if (!this.editMode) {
					this.resetDrag()
				}
			},
			/**
			 * 搜索引擎变更后持久化
			 */
			changeEngine() {
				window.localStorage.setItem('tab:searchEngine', this.searchEngine)
			},
			/**
			 * 使用搜索引擎搜索
			 */
			submitSearch() {
				const query = this.keyword.trim()
				if (!query) {
					return
				}
				const engine = this.searchEngines.find(item => item.key === this.searchEngine) || SEARCH_ENGINES[0]
				window.open(engine.url + encodeURIComponent(query), '_blank', 'noopener')
			},
			/**
			 * 查询分组下的全部书签
			 *
			 * @param {Number} categoryId 分组 ID
			 * @returns {Array} 书签列表
			 */
			bookmarksIn(categoryId) {
				return this.bookmarks.filter(bookmark => bookmark.categoryId === categoryId)
			},
			/**
			 * 查询分组下符合关键词的书签
			 *
			 * @param {Number} categoryId 分组 ID
			 * @returns {Array} 过滤后的书签列表
			 */
			visibleBookmarks(categoryId) {
				const list = this.bookmarksIn(categoryId)
				const keyword = this.keyword.trim().toLowerCase()
				if (!keyword) {
					return list
				}
				return list.filter(bookmark => {
					const title = (bookmark.title || '').toLowerCase()
					const url = (bookmark.url || '').toLowerCase()
					const description = (bookmark.description || '').toLowerCase()
					return title.includes(keyword) || url.includes(keyword) || description.includes(keyword)
				})
			},
			/**
			 * 分组下书签总数
			 *
			 * @param {Number} categoryId 分组 ID
			 * @returns {Number} 数量
			 */
			countOf(categoryId) {
				return this.bookmarksIn(categoryId).length
			},
			/**
			 * 打开书签并上报点击
			 *
			 * <p>点击数更新到原始书签对象上，保证常用区排序实时响应。</p>
			 *
			 * @param {Object} bookmark 书签（可能是常用区的展示副本）
			 */
			openBookmark(bookmark) {
				window.open(bookmark.url, '_blank', 'noopener')
				clickBookmark(bookmark.id).then(res => {
					if (res.code === 200) {
						const original = this.bookmarks.find(item => item.id === bookmark.id)
						if (original) {
							original.clicks = (original.clicks || 0) + 1
						}
					}
				}).catch(() => {
					// 点击统计失败不影响打开书签
				})
			},
			/**
			 * 构建常用书签的展示数据（图标三级兜底：自定义/emoji/favicon/首字母）
			 *
			 * @param {Object} bookmark 原始书签
			 * @returns {Object} 含 iconType/iconValue/letter/letterColor 的展示对象
			 */
			buildFrequentItem(bookmark) {
				const icon = (bookmark.icon || '').trim()
				const failed = this.failedFrequentIcons.includes(bookmark.id)
				const title = (bookmark.title || '').trim()
				if (icon && /^(http|data):/i.test(icon)) {
					if (!failed) {
						return {...bookmark, iconType: 'img', iconValue: icon}
					}
				} else if (icon) {
					return {...bookmark, iconType: 'emoji', iconValue: icon}
				} else if (!failed) {
					try {
						const parsed = new URL(bookmark.url)
						return {...bookmark, iconType: 'img', iconValue: `${parsed.protocol}//${parsed.host}/favicon.ico`}
					} catch (e) {
						// 地址非法时走首字母兜底
					}
				}
				return {
					...bookmark,
					iconType: 'letter',
					iconValue: '',
					letter: title ? title.charAt(0).toUpperCase() : '?',
					letterColor: this.frequentLetterColor(title)
				}
			},
			/**
			 * 首字母色块背景，按标题哈希取色保证颜色稳定
			 *
			 * @param {String} title 书签标题
			 * @returns {String} 渐变色值
			 */
			frequentLetterColor(title) {
				let hash = 0
				for (let i = 0; i < title.length; i++) {
					hash = title.charCodeAt(i) + ((hash << 5) - hash)
				}
				const hue = Math.abs(hash) % 360
				return `linear-gradient(135deg, hsl(${hue}, 68%, 72%), hsl(${(hue + 28) % 360}, 66%, 58%))`
			},
			/**
			 * 常用区图标加载失败后回退首字母
			 *
			 * @param {Object} bookmark 展示对象
			 */
			handleFrequentIconError(bookmark) {
				if (!this.failedFrequentIcons.includes(bookmark.id)) {
					this.failedFrequentIcons = [...this.failedFrequentIcons, bookmark.id]
				}
			},
			/**
			 * 页面点击时在指针处浮现社会主义核心价值观词组
			 *
			 * <p>文字不拦截交互；弹窗内的点击不触发；尊重系统的减少动态偏好。</p>
			 *
			 * @param {MouseEvent} event 点击事件
			 */
			handleStageClick(event) {
				if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
					return
				}
				const target = event.target
				if (target && target.closest && target.closest('.bm-dialog, .gd')) {
					return
				}
				const text = CORE_VALUES[this.coreValueIndex % CORE_VALUES.length]
				this.coreValueIndex += 1
				const word = {
					id: this.wordSequence,
					text,
					style: {
						left: `${event.clientX}px`,
						top: `${event.clientY}px`
					}
				}
				this.wordSequence += 1
				this.floatingWords.push(word)
				if (this.floatingWords.length > MAX_FLOATING_WORDS) {
					const removed = this.floatingWords.shift()
					window.clearTimeout(removed.timer)
				}
				word.timer = window.setTimeout(() => {
					const index = this.floatingWords.indexOf(word)
					if (index > -1) {
						this.floatingWords.splice(index, 1)
					}
				}, WORD_LIFETIME)
			},
			/**
			 * 初始化背景粒子画布
			 *
			 * <p>按视口面积生成 36~96 个粒子，绘制粒子点与近距连线，鼠标附近额外连线。</p>
			 */
			initParticles() {
				if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
					return
				}
				const canvas = this.$refs.particles
				if (!canvas || !canvas.getContext) {
					return
				}
				this._particleCtx = canvas.getContext('2d')
				this._particlePointer = {x: -9999, y: -9999}
				this._particleParticles = []
				this.resizeParticles()
				this._particleResizeHandler = () => this.resizeParticles()
				this._particleMoveHandler = event => {
					this._particlePointer.x = event.clientX
					this._particlePointer.y = event.clientY
				}
				window.addEventListener('resize', this._particleResizeHandler)
				window.addEventListener('mousemove', this._particleMoveHandler)
				this._particleRaf = window.requestAnimationFrame(() => this.drawParticlesFrame())
			},
			/**
			 * 视口尺寸变化时重建画布与粒子
			 */
			resizeParticles() {
				const canvas = this.$refs.particles
				if (!canvas || !this._particleCtx) {
					return
				}
				const dpr = Math.min(2, window.devicePixelRatio || 1)
				const width = canvas.clientWidth
				const height = canvas.clientHeight
				if (!width || !height) {
					return
				}
				canvas.width = Math.round(width * dpr)
				canvas.height = Math.round(height * dpr)
				this._particleCtx.setTransform(dpr, 0, 0, dpr, 0, 0)
				this._particleParticles = this.createParticles(width, height)
			},
			/**
			 * 依据画布面积生成粒子集合
			 *
			 * @param {Number} width 画布宽度
			 * @param {Number} height 画布高度
			 * @returns {Array} 粒子集合
			 */
			createParticles(width, height) {
				const count = Math.max(36, Math.min(96, Math.round(width * height / 24000)))
				const particles = []
				for (let i = 0; i < count; i++) {
					particles.push({
						x: Math.random() * width,
						y: Math.random() * height,
						vx: (Math.random() - 0.5) * 0.36,
						vy: (Math.random() - 0.5) * 0.36,
						radius: 1 + Math.random() * 1.4,
						hue: 212 + Math.random() * 66,
						alpha: 0.32 + Math.random() * 0.42
					})
				}
				return particles
			},
			/**
			 * 绘制一帧粒子并调度下一帧
			 */
			drawParticlesFrame() {
				const ctx = this._particleCtx
				const canvas = this.$refs.particles
				const particles = this._particleParticles
				if (!ctx || !canvas || !particles) {
					return
				}
				const width = canvas.clientWidth
				const height = canvas.clientHeight
				ctx.clearRect(0, 0, width, height)
				particles.forEach(particle => {
					particle.x += particle.vx
					particle.y += particle.vy
					if (particle.x < -24) {
						particle.x = width + 24
					} else if (particle.x > width + 24) {
						particle.x = -24
					}
					if (particle.y < -24) {
						particle.y = height + 24
					} else if (particle.y > height + 24) {
						particle.y = -24
					}
				})
				for (let i = 0; i < particles.length; i++) {
					const one = particles[i]
					for (let j = i + 1; j < particles.length; j++) {
						const other = particles[j]
						const dx = one.x - other.x
						const dy = one.y - other.y
						const distance = Math.sqrt(dx * dx + dy * dy)
						if (distance < PARTICLE_LINK_DISTANCE) {
							ctx.strokeStyle = `rgba(140, 160, 255, ${(1 - distance / PARTICLE_LINK_DISTANCE) * 0.16})`
							ctx.lineWidth = 1
							ctx.beginPath()
							ctx.moveTo(one.x, one.y)
							ctx.lineTo(other.x, other.y)
							ctx.stroke()
						}
					}
				}
				particles.forEach(particle => {
					ctx.fillStyle = `hsla(${particle.hue}, 78%, 74%, ${particle.alpha})`
					ctx.beginPath()
					ctx.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2)
					ctx.fill()
				})
				const pointer = this._particlePointer
				if (pointer) {
					particles.forEach(particle => {
						const dx = particle.x - pointer.x
						const dy = particle.y - pointer.y
						const distance = Math.sqrt(dx * dx + dy * dy)
						if (distance < PARTICLE_POINTER_DISTANCE) {
							ctx.strokeStyle = `rgba(186, 128, 255, ${(1 - distance / PARTICLE_POINTER_DISTANCE) * 0.3})`
							ctx.lineWidth = 1
							ctx.beginPath()
							ctx.moveTo(particle.x, particle.y)
							ctx.lineTo(pointer.x, pointer.y)
							ctx.stroke()
						}
					})
				}
				this._particleRaf = window.requestAnimationFrame(() => this.drawParticlesFrame())
			},
			/**
			 * 销毁粒子画布与全局监听
			 */
			teardownParticles() {
				if (this._particleRaf) {
					window.cancelAnimationFrame(this._particleRaf)
					this._particleRaf = null
				}
				if (this._particleResizeHandler) {
					window.removeEventListener('resize', this._particleResizeHandler)
					this._particleResizeHandler = null
				}
				if (this._particleMoveHandler) {
					window.removeEventListener('mousemove', this._particleMoveHandler)
					this._particleMoveHandler = null
				}
				this._particleParticles = null
				this._particleCtx = null
			},
			/**
			 * 打开新增书签弹窗
			 *
			 * @param {Object} category 分组
			 */
			addBookmark(category) {
				this.dialogType = 'bookmark'
				this.dialogItem = null
				this.dialogCategoryId = category.id
				this.dialogVisible = true
			},
			/**
			 * 打开编辑书签弹窗
			 *
			 * @param {Object} bookmark 书签
			 */
			editBookmark(bookmark) {
				this.dialogType = 'bookmark'
				this.dialogItem = bookmark
				this.dialogCategoryId = bookmark.categoryId
				this.dialogVisible = true
			},
			/**
			 * 删除书签
			 *
			 * @param {Object} bookmark 书签
			 */
			removeBookmark(bookmark) {
				this.$confirm(`确定删除书签「${bookmark.title}」？`, '删除书签', {
					type: 'warning',
					confirmButtonText: '删除',
					cancelButtonText: '取消'
				}).then(() => {
					deleteBookmarkApi(bookmark.id).then(res => {
						if (res.code !== 200) {
							this.msgError(res.msg || '删除失败')
							return
						}
						this.msgSuccess('删除成功')
						this.fetchData()
					}).catch(() => {
						this.msgError('删除失败，请检查登录状态')
					})
				}).catch(() => {
					// 用户取消删除
				})
			},
			/**
			 * 打开新增分组弹窗
			 */
			addCategory() {
				this.dialogType = 'category'
				this.dialogItem = null
				this.dialogCategoryId = null
				this.dialogVisible = true
			},
			/**
			 * 打开编辑分组弹窗
			 *
			 * @param {Object} category 分组
			 */
			editCategory(category) {
				this.dialogType = 'category'
				this.dialogItem = category
				this.dialogCategoryId = null
				this.dialogVisible = true
			},
			/**
			 * 删除分组
			 *
			 * @param {Object} category 分组
			 */
			removeCategory(category) {
				this.$confirm(`确定删除分组「${category.name}」？分组下存在书签时将无法删除。`, '删除分组', {
					type: 'warning',
					confirmButtonText: '删除',
					cancelButtonText: '取消'
				}).then(() => {
					deleteCategoryApi(category.id).then(res => {
						if (res.code !== 200) {
							this.msgError(res.msg || '删除失败')
							return
						}
						this.msgSuccess('删除成功')
						this.fetchData()
					}).catch(() => {
						this.msgError('删除失败，请检查登录状态')
					})
				}).catch(() => {
					// 用户取消删除
				})
			},
			/**
			 * 循环切换分组尺寸并提交后端
			 *
			 * @param {Object} category 分组
			 */
			resizeCategory(category) {
				const current = category.size || 'small'
				const index = SIZE_ORDER.indexOf(current)
				const next = SIZE_ORDER[(index + 1) % SIZE_ORDER.length]
				this.$set(category, 'size', next)
				updateCategorySizeApi(category.id, next).then(res => {
					if (res.code !== 200) {
						this.msgError(res.msg || '尺寸修改失败')
						this.$set(category, 'size', current)
					}
				}).catch(() => {
					this.msgError('尺寸修改失败，请检查登录状态')
					this.$set(category, 'size', current)
				})
			},
			/**
			 * 打开分组全量书签弹窗
			 *
			 * @param {Object} category 分组
			 */
			openGroupDialog(category) {
				this.groupDialogCategory = category
				this.groupDialogVisible = true
			},
			/**
			 * 关闭分组弹窗
			 */
			closeGroupDialog() {
				this.groupDialogVisible = false
			},
			/**
			 * 关闭编辑弹窗
			 */
			closeDialog() {
				this.dialogVisible = false
			},
			/**
			 * 保存弹窗提交的数据
			 *
			 * @param {Object} payload 表单数据
			 */
			handleDialogSave(payload) {
				this.saving = true
				let request
				if (this.dialogType === 'bookmark') {
					request = payload.id ? updateBookmarkApi(payload) : saveBookmarkApi(payload)
				} else {
					request = payload.id ? updateCategoryApi(payload) : saveCategoryApi(payload)
				}
				request.then(res => {
					if (res.code !== 200) {
						this.msgError(res.msg || '保存失败')
						return
					}
					this.dialogVisible = false
					this.msgSuccess(res.msg || '保存成功')
					this.fetchData()
				}).catch(() => {
					this.msgError('保存失败，请检查登录状态')
				}).finally(() => {
					this.saving = false
				})
			},
			/**
			 * 导出 JSON 备份文件
			 */
			exportData() {
				exportBookmarksApi().then(res => {
					if (res.code !== 200) {
						this.msgError(res.msg || '导出失败')
						return
					}
					const blob = new Blob([JSON.stringify(res.data, null, 2)], {type: 'application/json;charset=utf-8'})
					const objectUrl = URL.createObjectURL(blob)
					const link = document.createElement('a')
					link.href = objectUrl
					link.download = `bookmarks-${this.dateStamp}.json`
					document.body.appendChild(link)
					link.click()
					document.body.removeChild(link)
					window.setTimeout(() => URL.revokeObjectURL(objectUrl), 500)
					this.msgSuccess('导出成功')
				}).catch(() => {
					this.msgError('导出失败，请检查登录状态')
				})
			},
			/**
			 * 构建行×列的占位矩阵
			 *
			 * @param {Number} rows 行数
			 * @param {Number} cols 列数
			 * @returns {Array<Array<Boolean>>} 占位矩阵
			 */
			makeCells(rows, cols) {
				return Array.from({length: rows}, () => Array.from({length: cols}, () => false))
			},
			/**
			 * 在占位矩阵中寻找首个可放下 w×h 的位置
			 *
			 * @param {Array<Array<Boolean>>} cells 占位矩阵
			 * @param {Number} rows 行数
			 * @param {Number} cols 列数
			 * @param {Number} w 宽度跨度
			 * @param {Number} h 高度跨度
			 * @returns {Object|null} 位置 {r, c}，找不到返回 null
			 */
			findPlacement(cells, rows, cols, w, h) {
				for (let r = 0; r + h <= rows; r++) {
					for (let c = 0; c + w <= cols; c++) {
						if (this.isAreaFree(cells, r, c, w, h)) {
							return {r, c}
						}
					}
				}
				return null
			},
			/**
			 * 判断矩阵中指定区域是否空闲
			 *
			 * @param {Array<Array<Boolean>>} cells 占位矩阵
			 * @param {Number} r 起始行
			 * @param {Number} c 起始列
			 * @param {Number} w 宽度跨度
			 * @param {Number} h 高度跨度
			 * @returns {Boolean} 是否空闲
			 */
			isAreaFree(cells, r, c, w, h) {
				for (let rr = r; rr < r + h; rr++) {
					for (let cc = c; cc < c + w; cc++) {
						if (cells[rr][cc]) {
							return false
						}
					}
				}
				return true
			},
			/**
			 * 标记矩阵中指定区域为已占用
			 *
			 * @param {Array<Array<Boolean>>} cells 占位矩阵
			 * @param {Number} r 起始行
			 * @param {Number} c 起始列
			 * @param {Number} w 宽度跨度
			 * @param {Number} h 高度跨度
			 */
			occupyCells(cells, r, c, w, h) {
				for (let rr = r; rr < r + h; rr++) {
					for (let cc = c; cc < c + w; cc++) {
						cells[rr][cc] = true
					}
				}
			},
			/**
			 * 监听仪表盘尺寸并测量网格容量
			 */
			setupDashObserver() {
				this.teardownDashObserver()
				const dash = this.$refs.dash
				if (!dash) {
					return
				}
				this.measureDashboard()
				if (window.ResizeObserver) {
					this.dashObserver = new ResizeObserver(() => this.measureDashboard())
					this.dashObserver.observe(dash)
				} else {
					window.addEventListener('resize', this.measureDashboard)
				}
			},
			/**
			 * 销毁仪表盘尺寸监听
			 */
			teardownDashObserver() {
				if (this.dashObserver) {
					this.dashObserver.disconnect()
					this.dashObserver = null
				}
				window.removeEventListener('resize', this.measureDashboard)
			},
			/**
			 * 依据仪表盘实际内容区域尺寸计算一页的列数与行数
			 *
			 * <p>clientWidth/clientHeight 包含内边距（左右 50px、底部 34px），需扣除后按内容区计算，
			 * 否则行列数会偏大导致分组被压缩到最小尺寸以下。</p>
			 */
			measureDashboard() {
				const dash = this.$refs.dash
				if (!dash) {
					return
				}
				const style = window.getComputedStyle(dash)
				const width = dash.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight)
				const height = dash.clientHeight - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom)
				const narrow = width < NARROW_WIDTH
				this.gridDims = {
					narrow,
					cols: narrow ? 1 : Math.min(MAX_GRID_COLS, Math.max(1, Math.floor((width + DASH_GAP) / (WIDGET_MIN_WIDTH + DASH_GAP)))),
					rows: narrow ? 1 : Math.min(MAX_GRID_ROWS, Math.max(1, Math.floor((height + DASH_GAP) / (WIDGET_MIN_HEIGHT + DASH_GAP))))
				}
			},
			/**
			 * 翻页（循环）
			 *
			 * @param {Number} delta 方向：-1 上一页 / 1 下一页
			 */
			turnPage(delta) {
				const total = this.pages.length
				if (!total) {
					return
				}
				this.pageIndex = (this.pageIndex + delta + total) % total
			},
			/**
			 * 拖拽悬停翻页箭头时延时切换页面
			 *
			 * @param {Number} delta 方向：-1 上一页 / 1 下一页
			 */
			dragEnterPage(delta) {
				if (!this.draggingBookmark && !this.draggingCategory) {
					return
				}
				this.clearPageTimer()
				this.pageTimer = window.setTimeout(() => {
					const next = this.pageIndex + delta
					if (next >= 0 && next < this.pages.length) {
						this.pageIndex = next
					}
				}, PAGE_SWITCH_DELAY)
			},
			/**
			 * 清除翻页延时器
			 */
			clearPageTimer() {
				if (this.pageTimer) {
					window.clearTimeout(this.pageTimer)
					this.pageTimer = null
				}
			},
			/**
			 * 开始拖拽书签
			 *
			 * @param {Object} bookmark 书签
			 */
			handleBookmarkDragStart(bookmark) {
				this.draggingBookmark = bookmark
				this.draggingCategory = null
			},
			/**
			 * 开始拖拽分组
			 *
			 * @param {Object} category 分组
			 */
			handleCategoryDragStart(category) {
				this.draggingCategory = category
				this.draggingBookmark = null
			},
			/**
			 * 拖拽物放置到某张书签卡片上
			 *
			 * @param {Object} payload {target, category, insertAfter} 目标书签、所属分组与插入方向
			 */
			handleCardDrop(payload) {
				const draggedCategory = this.draggingCategory
				if (draggedCategory) {
					this.resetDrag()
					this.moveCategory(draggedCategory, payload.category, payload.insertAfter)
					return
				}
				const dragged = this.draggingBookmark
				this.resetDrag()
				if (!this.editMode || !dragged || !payload.target || dragged.id === payload.target.id) {
					return
				}
				if (dragged.categoryId === payload.target.categoryId) {
					this.reorderBookmarkBefore(dragged, payload.target)
				} else {
					this.moveBookmarkToCategory(dragged, payload.category)
				}
			},
			/**
			 * 拖拽物放置到分组空白区域
			 *
			 * @param {Object} payload {category, insertAfter} 目标分组与插入方向
			 */
			handleBodyDrop(payload) {
				const draggedCategory = this.draggingCategory
				const draggedBookmark = this.draggingBookmark
				this.resetDrag()
				if (!this.editMode) {
					return
				}
				if (draggedCategory) {
					this.moveCategory(draggedCategory, payload.category, payload.insertAfter)
				} else if (draggedBookmark) {
					if (draggedBookmark.categoryId === payload.category.id) {
						this.moveBookmarkToCategoryEnd(draggedBookmark, payload.category)
					} else {
						this.moveBookmarkToCategory(draggedBookmark, payload.category)
					}
				}
			},
			/**
			 * 将书签移动到目标书签之前（同分组内）
			 *
			 * @param {Object} dragged 被拖拽的书签
			 * @param {Object} target 目标书签
			 */
			reorderBookmarkBefore(dragged, target) {
				const list = this.bookmarksIn(target.categoryId).slice()
				const fromIndex = list.findIndex(bookmark => bookmark.id === dragged.id)
				if (fromIndex < 0) {
					return
				}
				list.splice(fromIndex, 1)
				const toIndex = list.findIndex(bookmark => bookmark.id === target.id)
				list.splice(toIndex < 0 ? list.length : toIndex, 0, dragged)
				this.applyCategoryOrder(target.categoryId, list)
			},
			/**
			 * 调整分组顺序
			 *
			 * <p>根据拖放落点把被拖拽分组插入到目标分组之前或之后，实现位置控制。</p>
			 *
			 * @param {Object} dragged 被拖拽的分组
			 * @param {Object} target 目标分组
			 * @param {Boolean} insertAfter 是否插入到目标分组之后
			 */
			moveCategory(dragged, target, insertAfter) {
				if (!this.editMode || dragged.id === target.id) {
					return
				}
				const list = this.categories.slice()
				const fromIndex = list.findIndex(category => category.id === dragged.id)
				if (fromIndex < 0) {
					return
				}
				list.splice(fromIndex, 1)
				let toIndex = list.findIndex(category => category.id === target.id)
				if (toIndex < 0) {
					toIndex = list.length
				} else if (insertAfter) {
					toIndex += 1
				}
				list.splice(toIndex, 0, dragged)
				this.categories = list
				list.forEach((category, index) => {
					category.sort = index
				})
				sortCategoriesApi(list.map(category => category.id)).then(res => {
					if (res.code !== 200) {
						this.msgError(res.msg || '排序失败')
						this.fetchData()
					}
				}).catch(() => {
					this.msgError('排序失败，请检查登录状态')
					this.fetchData()
				})
			},
			/**
			 * 将书签移动到分组末尾（同分组内）
			 *
			 * @param {Object} dragged 被拖拽的书签
			 * @param {Object} target 目标分组
			 */
			moveBookmarkToCategoryEnd(dragged, target) {
				const list = this.bookmarksIn(target.id).filter(bookmark => bookmark.id !== dragged.id)
				list.push(dragged)
				this.applyCategoryOrder(target.id, list)
			},
			/**
			 * 将书签移动到其他分组（追加到目标分组末尾）
			 *
			 * @param {Object} dragged 被拖拽的书签
			 * @param {Object} category 目标分组
			 */
			moveBookmarkToCategory(dragged, category) {
				updateBookmarkApi({...dragged, categoryId: category.id}).then(res => {
					if (res.code !== 200) {
						this.msgError(res.msg || '移动失败')
						return
					}
					this.msgSuccess(`已移动到「${category.name}」`)
					this.fetchData()
				}).catch(() => {
					this.msgError('移动失败，请检查登录状态')
				})
			},
			/**
			 * 应用分组内新顺序并提交后端
			 *
			 * @param {Number} categoryId 分组 ID
			 * @param {Array} list 新顺序的书签列表
			 */
			applyCategoryOrder(categoryId, list) {
				const others = this.bookmarks.filter(bookmark => bookmark.categoryId !== categoryId)
				this.bookmarks = others.concat(list)
				list.forEach((bookmark, index) => {
					bookmark.sort = index
				})
				sortBookmarksApi(list.map(bookmark => bookmark.id)).then(res => {
					if (res.code !== 200) {
						this.msgError(res.msg || '排序失败')
						this.fetchData()
					}
				}).catch(() => {
					this.msgError('排序失败，请检查登录状态')
					this.fetchData()
				})
			},
			/**
			 * 重置拖拽状态
			 */
			resetDrag() {
				this.draggingBookmark = null
				this.draggingCategory = null
				this.clearPageTimer()
			}
		}
	}
</script>

<style scoped>
	.tab {
		position: fixed;
		inset: 0;
		display: flex;
		flex-direction: column;
		overflow: hidden;
		background:
			radial-gradient(900px 640px at 12% 6%, rgba(104, 132, 255, 0.32), transparent 62%),
			radial-gradient(820px 620px at 88% 18%, rgba(186, 108, 255, 0.28), transparent 62%),
			radial-gradient(880px 680px at 48% 104%, rgba(255, 122, 178, 0.2), transparent 64%),
			linear-gradient(160deg, #0f1326 0%, #151a33 42%, #1d1230 100%);
		color: #e9edff;
		font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", sans-serif;
		-webkit-font-smoothing: antialiased;
	}

	.tab__bg {
		position: fixed;
		inset: 0;
		overflow: hidden;
		pointer-events: none;
	}

	.tab__particles {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;
	}

	.tab__word {
		position: fixed;
		z-index: 10;
		pointer-events: none;
		transform: translate(-50%, -50%);
		background: linear-gradient(180deg, #ffd98a, #ff9a6b 55%, #ff6b6b);
		-webkit-background-clip: text;
		background-clip: text;
		-webkit-text-fill-color: transparent;
		color: transparent;
		font-size: 22px;
		font-weight: 700;
		letter-spacing: 6px;
		text-indent: 6px;
		white-space: nowrap;
		filter: drop-shadow(0 0 12px rgba(255, 170, 90, 0.45));
		animation: tab-word-rise 1.3s cubic-bezier(0.22, 0.68, 0.36, 1) forwards;
	}

	@keyframes tab-word-rise {
		0% {
			opacity: 0;
			transform: translate(-50%, -50%) scale(0.4);
		}
		22% {
			opacity: 1;
			transform: translate(-50%, -46%) scale(1.06);
		}
		60% {
			opacity: 1;
			transform: translate(-50%, -78%) scale(1);
		}
		100% {
			opacity: 0;
			transform: translate(-50%, -130%) scale(0.92);
		}
	}

	.tab__bg::before {
		content: '';
		position: absolute;
		inset: 0;
		background:
			repeating-linear-gradient(0deg, rgba(255, 255, 255, 0.035) 0 1px, transparent 1px 56px),
			repeating-linear-gradient(90deg, rgba(255, 255, 255, 0.035) 0 1px, transparent 1px 56px);
		-webkit-mask-image: radial-gradient(ellipse 92% 82% at 50% 18%, #000 24%, transparent 72%);
		mask-image: radial-gradient(ellipse 92% 82% at 50% 18%, #000 24%, transparent 72%);
	}

	.tab__blob {
		position: absolute;
		border-radius: 50%;
		filter: blur(90px);
		opacity: 0.5;
		mix-blend-mode: screen;
	}

	.tab__blob--1 {
		top: -180px;
		left: -120px;
		width: 520px;
		height: 520px;
		background: #4d6bff;
		animation: tab-float-one 22s ease-in-out infinite;
	}

	.tab__blob--2 {
		top: 16%;
		right: -180px;
		width: 560px;
		height: 560px;
		background: #a355ff;
		animation: tab-float-two 26s ease-in-out infinite;
	}

	.tab__blob--3 {
		bottom: -200px;
		left: 14%;
		width: 540px;
		height: 540px;
		background: #ff5f97;
		animation: tab-float-three 24s ease-in-out infinite;
	}

	@keyframes tab-float-one {
		0%, 100% {
			transform: translate(0, 0) scale(1);
		}
		50% {
			transform: translate(56px, 64px) scale(1.08);
		}
	}

	@keyframes tab-float-two {
		0%, 100% {
			transform: translate(0, 0) scale(1);
		}
		50% {
			transform: translate(-62px, 48px) scale(1.06);
		}
	}

	@keyframes tab-float-three {
		0%, 100% {
			transform: translate(0, 0) scale(1);
		}
		50% {
			transform: translate(46px, -56px) scale(1.1);
		}
	}

	.tab__toolbar {
		position: fixed;
		top: 20px;
		right: 24px;
		z-index: 20;
		display: flex;
		flex-wrap: wrap;
		justify-content: flex-end;
		gap: 10px;
		max-width: 60vw;
	}

	.tab__tool {
		padding: 8px 16px;
		border: 1px solid rgba(255, 255, 255, 0.16);
		border-radius: 999px;
		background: rgba(255, 255, 255, 0.08);
		backdrop-filter: blur(16px);
		color: rgba(232, 238, 255, 0.82);
		cursor: pointer;
		font-size: 13px;
		transition: background 0.2s ease, transform 0.2s ease, color 0.2s ease, border-color 0.2s ease;
	}

	.tab__tool:hover {
		background: rgba(255, 255, 255, 0.16);
		border-color: rgba(150, 175, 255, 0.4);
		color: #fff;
		transform: translateY(-1px);
	}

	.tab__tool--active {
		background: linear-gradient(135deg, #6d7dff, #b06bff);
		border-color: transparent;
		color: #fff;
		box-shadow: 0 10px 26px rgba(109, 125, 255, 0.42);
	}

	.tab__tool--active:hover {
		background: linear-gradient(135deg, #7b89ff, #bb7bff);
		box-shadow: 0 12px 30px rgba(109, 125, 255, 0.5);
	}

	.tab__main {
		flex: 1;
		min-height: 0;
		display: flex;
		flex-direction: column;
		width: min(1480px, 94vw);
		margin: 0 auto;
		padding: 68px 0 20px;
	}

	.tab__clock {
		flex: none;
		margin-bottom: 16px;
		text-align: center;
	}

	.tab__time {
		display: inline-block;
		background: linear-gradient(180deg, #ffffff 22%, rgba(188, 205, 255, 0.74) 100%);
		-webkit-background-clip: text;
		background-clip: text;
		-webkit-text-fill-color: transparent;
		color: transparent;
		font-size: clamp(46px, 6vw, 72px);
		font-weight: 200;
		letter-spacing: 3px;
		line-height: 1;
		filter: drop-shadow(0 10px 34px rgba(112, 148, 255, 0.32));
	}

	.tab__colon {
		background: linear-gradient(180deg, #ffffff 22%, rgba(188, 205, 255, 0.74) 100%);
		-webkit-background-clip: text;
		background-clip: text;
		-webkit-text-fill-color: transparent;
		color: transparent;
		animation: tab-colon-blink 1s ease-in-out infinite;
	}

	@keyframes tab-colon-blink {
		0%, 100% {
			opacity: 1;
		}
		50% {
			opacity: 0.18;
		}
	}

	.tab__seconds {
		margin-left: 12px;
		background: linear-gradient(180deg, rgba(255, 255, 255, 0.92), rgba(160, 178, 255, 0.68));
		-webkit-background-clip: text;
		background-clip: text;
		-webkit-text-fill-color: transparent;
		color: transparent;
		font-size: 0.38em;
		font-weight: 300;
		letter-spacing: 2px;
		font-variant-numeric: tabular-nums;
	}

	.tab__date {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 12px;
		margin-top: 10px;
		color: rgba(206, 218, 255, 0.72);
		font-size: 13px;
		letter-spacing: 0.6px;
	}

	.tab__greeting {
		padding-left: 14px;
		border-left: 1px solid rgba(206, 218, 255, 0.24);
	}

	.tab__search {
		position: relative;
		display: flex;
		align-items: center;
		gap: 12px;
		flex: none;
		width: min(620px, 100%);
		height: 52px;
		margin: 0 auto 22px;
		padding: 0 22px;
		border-radius: 999px;
		background: rgba(255, 255, 255, 0.07);
		border: 1px solid rgba(255, 255, 255, 0.14);
		box-shadow: 0 18px 46px rgba(3, 6, 20, 0.5);
		backdrop-filter: blur(20px);
		transition: box-shadow 0.25s ease, background 0.25s ease, border-color 0.25s ease,
			transform 0.25s cubic-bezier(0.22, 0.68, 0.36, 1);
	}

	.tab__search::before {
		content: '';
		position: absolute;
		inset: -1px;
		border-radius: inherit;
		padding: 1.5px;
		background: linear-gradient(90deg, #6d7dff, #b06bff, #ff6bb0, #6d7dff);
		background-size: 300% 100%;
		-webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
		-webkit-mask-composite: xor;
		mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
		mask-composite: exclude;
		opacity: 0;
		pointer-events: none;
		transition: opacity 0.3s ease;
	}

	.tab__search:hover {
		transform: translateY(-1px);
		border-color: rgba(255, 255, 255, 0.22);
	}

	.tab__search:focus-within {
		background: rgba(255, 255, 255, 0.12);
		border-color: rgba(140, 164, 255, 0.5);
		box-shadow: 0 0 0 4px rgba(112, 144, 255, 0.16), 0 26px 58px rgba(3, 6, 20, 0.55);
	}

	.tab__search:focus-within::before {
		opacity: 1;
		animation: tab-border-flow 3s linear infinite;
	}

	@keyframes tab-border-flow {
		0% {
			background-position: 0% 50%;
		}
		100% {
			background-position: 100% 50%;
		}
	}

	.tab__search-icon {
		flex: none;
		color: rgba(190, 202, 245, 0.7);
		transition: color 0.25s ease, transform 0.25s ease;
	}

	.tab__search:focus-within .tab__search-icon {
		color: #a8b8ff;
		transform: scale(1.15) rotate(-6deg);
	}

	.tab__search-input {
		flex: 1;
		min-width: 0;
		border: none;
		background: transparent;
		color: #eaf0ff;
		font: inherit;
		font-size: 15px;
		outline: none;
	}

	.tab__search-input::placeholder {
		color: rgba(180, 192, 235, 0.5);
	}

	.tab__engine {
		flex: none;
		padding: 4px 6px;
		border: none;
		border-left: 1px solid rgba(255, 255, 255, 0.14);
		background: transparent;
		color: rgba(206, 216, 250, 0.75);
		cursor: pointer;
		font: inherit;
		font-size: 13px;
		outline: none;
	}

	.tab__engine option {
		background: #1a1f38;
		color: #e6ebff;
	}

	.tab__frequent {
		flex: none;
		display: flex;
		align-items: center;
		gap: 12px;
		width: min(1280px, 100%);
		margin: 0 auto 18px;
		min-width: 0;
	}

	.tab__frequent-label {
		flex: none;
		color: rgba(206, 218, 255, 0.62);
		font-size: 12px;
		letter-spacing: 2px;
		white-space: nowrap;
	}

	.tab__frequent-list {
		display: flex;
		align-items: center;
		gap: 8px;
		min-width: 0;
		overflow-x: auto;
		padding: 3px 2px;
		scrollbar-width: none;
		-ms-overflow-style: none;
	}

	.tab__frequent-list::-webkit-scrollbar {
		display: none;
	}

	.tab__frequent-item {
		flex: none;
		display: inline-flex;
		align-items: center;
		gap: 8px;
		max-width: 172px;
		height: 38px;
		padding: 0 15px 0 11px;
		border: 1px solid rgba(255, 255, 255, 0.12);
		border-radius: 999px;
		background: rgba(255, 255, 255, 0.055);
		backdrop-filter: blur(12px);
		color: rgba(232, 238, 255, 0.86);
		cursor: pointer;
		font-size: 13px;
		transition: transform 0.22s cubic-bezier(0.22, 0.68, 0.36, 1), background 0.22s ease,
			border-color 0.22s ease, color 0.22s ease, box-shadow 0.22s ease;
	}

	.tab__frequent-item:hover {
		transform: translateY(-2px);
		border-color: rgba(140, 164, 255, 0.48);
		background: rgba(255, 255, 255, 0.12);
		color: #fff;
		box-shadow: 0 12px 26px rgba(3, 6, 20, 0.4);
	}

	.tab__frequent-icon {
		flex: none;
		display: flex;
		align-items: center;
		justify-content: center;
		width: 22px;
		height: 22px;
		overflow: hidden;
	}

	.tab__frequent-icon img {
		width: 16px;
		height: 16px;
		object-fit: contain;
	}

	.tab__frequent-emoji {
		font-size: 14px;
		line-height: 1;
	}

	.tab__frequent-letter {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 100%;
		height: 100%;
		border-radius: 7px;
		color: #fff;
		font-size: 11px;
		font-weight: 600;
	}

	.tab__frequent-title {
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.tab__state {
		flex: 1;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 14px;
		padding: 40px 0;
		color: rgba(196, 208, 245, 0.62);
		font-size: 14px;
		text-align: center;
	}

	.tab__state p {
		margin: 0;
	}

	.tab__state-hint {
		color: rgba(180, 192, 230, 0.46);
		font-size: 12px;
	}

	.tab__state--inline {
		flex: none;
		display: block;
		position: absolute;
		top: 8px;
		left: 0;
		right: 0;
		z-index: 6;
		padding: 0;
		text-align: center;
		pointer-events: none;
	}

	.tab__btn {
		padding: 9px 22px;
		border: 1px solid rgba(140, 164, 255, 0.4);
		border-radius: 999px;
		background: rgba(255, 255, 255, 0.1);
		color: #b9c7ff;
		cursor: pointer;
		font-size: 13px;
		transition: background 0.2s ease;
	}

	.tab__btn:hover {
		background: rgba(255, 255, 255, 0.18);
	}

	.tab__dash {
		position: relative;
		flex: 1;
		min-height: 0;
		padding: 0 50px 34px;
	}

	.tab__page {
		display: grid;
		gap: 20px;
		height: 100%;
	}

	.tab__page--narrow {
		height: auto;
	}

	.tab__arrow {
		position: absolute;
		top: 50%;
		transform: translateY(-50%);
		z-index: 6;
		width: 38px;
		height: 62px;
		border: 1px solid rgba(255, 255, 255, 0.14);
		border-radius: 14px;
		background: rgba(20, 24, 46, 0.55);
		backdrop-filter: blur(12px);
		color: rgba(220, 230, 255, 0.75);
		cursor: pointer;
		font-size: 22px;
		line-height: 1;
		transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
	}

	.tab__arrow:hover {
		background: rgba(255, 255, 255, 0.12);
		border-color: rgba(140, 164, 255, 0.45);
		color: #fff;
	}

	.tab__arrow--prev {
		left: 0;
	}

	.tab__arrow--next {
		right: 0;
	}

	.tab__dots {
		position: absolute;
		left: 0;
		right: 0;
		bottom: 2px;
		display: flex;
		justify-content: center;
		gap: 8px;
	}

	.tab__dot {
		width: 8px;
		height: 8px;
		padding: 0;
		border: none;
		border-radius: 999px;
		background: rgba(255, 255, 255, 0.22);
		cursor: pointer;
		transition: width 0.25s ease, background 0.25s ease;
	}

	.tab__dot:hover {
		background: rgba(255, 255, 255, 0.4);
	}

	.tab__dot--active {
		width: 22px;
		background: linear-gradient(135deg, #6d7dff, #b06bff);
	}

	.tab__add-category {
		position: absolute;
		right: 0;
		bottom: 0;
		transform: translateX(10%);
		padding: 9px 22px;
		border: 1px dashed rgba(255, 255, 255, 0.26);
		border-radius: 999px;
		background: rgba(255, 255, 255, 0.05);
		color: rgba(206, 218, 252, 0.72);
		cursor: pointer;
		font-size: 13px;
		letter-spacing: 1px;
		backdrop-filter: blur(12px);
		transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
	}

	.tab__add-category:hover {
		border-color: rgba(140, 164, 255, 0.6);
		background: rgba(140, 164, 255, 0.12);
		color: #cdd9ff;
		box-shadow: 0 10px 24px rgba(3, 6, 20, 0.4);
	}

	.tab-page-enter-active,
	.tab-page-leave-active {
		transition: opacity 0.25s ease, transform 0.25s ease;
	}

	.tab-page-enter {
		opacity: 0;
		transform: translateX(26px);
	}

	.tab-page-leave-to {
		opacity: 0;
		transform: translateX(-26px);
	}

	@media (max-width: 640px), (max-height: 600px) {
		.tab {
			overflow-y: auto;
		}

		.tab__main {
			flex: none;
			min-height: 100%;
		}

		.tab__dash {
			flex: none;
			min-height: 400px;
		}
	}

	@media (max-width: 640px) {
		.tab__toolbar {
			top: 12px;
			right: 12px;
			left: 12px;
			justify-content: flex-end;
			max-width: none;
		}

		.tab__main {
			width: 92vw;
			padding: 64px 0 24px;
		}

		.tab__time {
			font-size: 46px;
		}

		.tab__date {
			flex-direction: column;
			gap: 6px;
		}

		.tab__greeting {
			padding-left: 0;
			border-left: none;
		}

		.tab__search {
			height: 48px;
			padding: 0 16px;
		}

		.tab__search-input {
			font-size: 14px;
		}

		.tab__dash {
			padding: 0 0 10px;
		}

		.tab__add-category {
			position: static;
			width: 100%;
			margin-top: 14px;
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.tab__blob {
			animation: none;
		}

		.tab__particles,
		.tab__word {
			display: none;
		}

		.tab__colon,
		.tab__search:focus-within::before {
			animation: none;
		}

		.tab__search,
		.tab__search-icon,
		.tab__frequent-item,
		.tab__tool {
			transition: none;
		}

		.tab__search:hover {
			transform: none;
		}
	}
</style>
